import { ContentState } from "draft-js";
import { List } from "immutable";
import { ChapterDraft } from "@domain/lms/course/courseDraft";
import {
	AttachmentSection,
	CourseSection,
	CourseSectionType,
	ImageSection,
	LoadingSection,
	SubtitleSection,
	TextSection,
	VideoSection,
} from "@domain/lms/course/courseSection";
import { ExerciseChoiceDraft, ExerciseDraft } from "@domain/lms/course/exercise/exerciseDraft";
import { withId } from "@utils/domain";
import { ExerciseController } from "@ui/pages/lms/courseEditorPage/exerciseEditor/exerciseController";
import { AttachmentSectionController } from "@ui/pages/lms/courseEditorPage/sectionEditor/attachmentSectionEditor/attachmentSectionController";
import { ImageSectionController } from "@ui/pages/lms/courseEditorPage/sectionEditor/imageSectionEditor/imageSectionController";
import { SubtitleSectionController } from "@ui/pages/lms/courseEditorPage/sectionEditor/subtitleSectionEditor/subtitleSectionController";
import { TextSectionController } from "@ui/pages/lms/courseEditorPage/sectionEditor/textSectionEditor/textSectionController";
import { VideoSectionController } from "@ui/pages/lms/courseEditorPage/sectionEditor/videoSectionEditor/videoSectionController";

export class ChapterController {
	constructor(
		private readonly chapterUpdater: (update: (chap: ChapterDraft) => ChapterDraft) => void,
		public readonly removeChapter: () => void
	) {}

	private updateSections(
		update: (sections: List<CourseSection | LoadingSection>) => List<CourseSection | LoadingSection>
	): void {
		this.chapterUpdater((current) => ({ ...current, sections: update(current.sections) }));
	}
	private updateExercises(update: (exercises: List<ExerciseDraft>) => List<ExerciseDraft>): void {
		this.chapterUpdater((current) => ({ ...current, exercises: update(current.exercises) }));
	}

	addSection(type: "TEXT" | "SUBTITLE"): void;
	addSection(type: "IMAGE", assetId: string): void;
	addSection(type: "PDF", assetId: string): void;
	addSection(type: "VIDEO", assetId?: string): void;
	addSection(type: "ATTACHMENT", assetId: string, fileName: string): void;
	addSection(type: CourseSectionType, assetId?: string, fileName?: string): void {
		this.updateSections((current) =>
			current.push(
				withId(
					type === "TEXT"
						? { type, content: ContentState.createFromText("") }
						: type === "VIDEO"
							? { type, ...(assetId ? { assetId } : { url: "" }) }
							: type === "SUBTITLE"
								? { type, title: "" }
								: type === "ATTACHMENT"
									? { type, assetId: assetId!, title: fileName! }
									: { type, assetId: assetId! }
				)
			)
		);
	}

	addLoadingSection(taskId: string): void {
		this.updateSections((current) => current.push({ type: "LOADING", id: taskId }));
	}

	addOrReplaceSection(type: "VIDEO", assetId: string, taskId: string): void {
		this.updateSections((current) => {
			const loadingIndex = current.findIndex((section) => section.type === "LOADING" && section.id === taskId);
			if (loadingIndex >= 0) {
				return current.set(loadingIndex, withId({ type, assetId }));
			}
			return current.push(withId({ type, assetId }));
		});
	}

	addExercise(): void {
		this.updateExercises((current) =>
			current.push(
				withId({
					title: "",
					choices: List([withId({ text: "", valid: false }) as ExerciseChoiceDraft]),
					type: "UNIQUE",
					comment: ContentState.createFromText(""),
					editing: true,
				})
			)
		);
	}

	setTitle(title: string): void {
		this.chapterUpdater((current) => ({ ...current, title }));
	}

	private updateSection<T extends CourseSection>(id: string, update: (section: T) => T): void {
		this.updateSections((current) => current.map((s) => (s.id === id ? update(s as T) : s)));
	}
	private updateExercise(id: string, update: (exercise: ExerciseDraft) => ExerciseDraft): void {
		this.updateExercises((current) => current.map((ex) => (ex.id === id ? update(ex) : ex)));
	}

	removeSection(id: string): void {
		this.updateSections((current) => {
			const index = current.findIndex((s) => s.id === id);
			if (index < 0) {
				return current;
			}
			return current.remove(index);
		});
	}
	private removeExercise(id: string): void {
		this.updateExercises((current) => {
			const index = current.findIndex((s) => s.id === id);
			if (index < 0) {
				return current;
			}
			return current.remove(index);
		});
	}

	videoSectionController(section: VideoSection): VideoSectionController {
		return new VideoSectionController(
			(updater) => this.updateSection(section.id, updater),
			() => this.removeSection(section.id)
		);
	}
	textSectionController(section: TextSection): TextSectionController {
		return new TextSectionController(
			(updater) => this.updateSection(section.id, updater),
			() => this.removeSection(section.id)
		);
	}
	imageSectionController(section: ImageSection): ImageSectionController {
		return new ImageSectionController(
			(updater) => this.updateSection(section.id, updater),
			() => this.removeSection(section.id)
		);
	}
	attachmentSectionController(section: AttachmentSection): AttachmentSectionController {
		return new AttachmentSectionController(
			(updater) => this.updateSection(section.id, updater),
			() => this.removeSection(section.id)
		);
	}
	subtitleSectionController(section: SubtitleSection): SubtitleSectionController {
		return new SubtitleSectionController(
			(updater) => this.updateSection(section.id, updater),
			() => this.removeSection(section.id)
		);
	}
	exerciseController(exercise: ExerciseDraft): ExerciseController {
		return new ExerciseController(
			(updater) => this.updateExercise(exercise.id, updater),
			() => this.removeExercise(exercise.id)
		);
	}
}
